import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setAccessToken, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserAvatar from "@/components/UserAvatar";
import UploadButton from "@/components/UploadButton";
import PositionsManager from "@/components/PositionsManager";
import { toast } from "sonner";
import { User, Lock, Sliders, Building2, ShieldCheck, Plus, Trash2, Moon, Sun, Globe, MapPin } from "lucide-react";

const TIMEZONES = ["America/Toronto", "America/Montreal", "America/Halifax", "America/Vancouver", "America/Winnipeg", "UTC"];

export default function Settings() {
  const { t, lang, setLang } = useT();
  const { user, setUser, theme, toggleTheme } = useAuth();
  const { role, isSuperAdmin, activeCenter, activeId, centers, reload } = useCenter();
  const navigate = useNavigate();
  const isAdmin = isSuperAdmin || role === "admin";

  const [p, setP] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [biz, setBiz] = useState({});
  const [myPositions, setMyPositions] = useState([]);

  useEffect(() => {
    if (user) setP({
      name: user.name || "", first_name: user.first_name || "", last_name: user.last_name || "",
      email: user.email || "", phone: user.phone || "",
      address: user.address || "", date_of_birth: user.date_of_birth || "",
      profile_picture: user.profile_picture || "", emergency_contacts: user.emergency_contacts || [],
      health_info: user.health_info || "",
    });
  }, [user]);
  useEffect(() => {
    if (activeCenter) setBiz({ name: activeCenter.name || "", address: activeCenter.address || "", timezone: activeCenter.timezone || "America/Toronto" });
  }, [activeCenter]);
  useEffect(() => { if (activeId) api.get("/positions/mine").then((r) => setMyPositions(r.data.positions || [])).catch(() => {}); }, [activeId]);

  const setPf = (k, v) => setP((f) => ({ ...f, [k]: v }));

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/auth/profile", p);
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      toast.success(t("profileUpdated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePassword = async () => {
    if (pw.new_password.length < 6) return toast.error(t("passwordTooShort"));
    if (pw.new_password !== pw.confirm) return toast.error(t("passwordsDontMatch"));
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success(t("passwordChanged"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveBusiness = async () => {
    try {
      await api.put(`/centers/${activeId}`, biz);
      await reload();
      toast.success(t("businessUpdated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addContact = () => setPf("emergency_contacts", [...(p.emergency_contacts || []), { name: "", relationship: "", phone: "" }]);
  const setContact = (i, k, v) => setPf("emergency_contacts", p.emergency_contacts.map((c, j) => j === i ? { ...c, [k]: v } : c));
  const rmContact = (i) => setPf("emergency_contacts", p.emergency_contacts.filter((_, j) => j !== i));

  const roleLabel = isSuperAdmin ? "Super Admin" : role === "admin" ? t("owner") : role === "supervisor" ? t("supervisor") : t("employee");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2"><Sliders className="w-7 h-7 text-primary" />{t("settings")}</h1>
        <p className="text-muted-foreground mt-1">{roleLabel}</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1.5" />{t("profileTab")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><Lock className="w-4 h-4 mr-1.5" />{t("security")}</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences"><Sliders className="w-4 h-4 mr-1.5" />{t("preferences")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="business" data-testid="tab-business"><Building2 className="w-4 h-4 mr-1.5" />{t("business")}</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="system" data-testid="tab-system"><ShieldCheck className="w-4 h-4 mr-1.5" />{t("system")}</TabsTrigger>}
        </TabsList>

        {/* PROFILE */}
        <TabsContent value="profile" className="space-y-4 mt-6">
          <div className="flex items-center gap-4">
            <UserAvatar name={`${p.first_name || ""} ${p.last_name || ""}`.trim() || p.name} src={p.profile_picture} size={64} />
            <UploadButton label={t("profilePicture")} accept="image/*" category="avatar" testId="upload-avatar" onUploaded={(d) => setPf("profile_picture", `file:${d.id}`)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">{t("firstName")}</Label><Input value={p.first_name || ""} onChange={(e) => setPf("first_name", e.target.value)} data-testid="profile-first-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("lastName")}</Label><Input value={p.last_name || ""} onChange={(e) => setPf("last_name", e.target.value)} data-testid="profile-last-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("email")}</Label><Input type="email" value={p.email || ""} onChange={(e) => setPf("email", e.target.value)} data-testid="profile-email" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("phone")}</Label><Input value={p.phone || ""} onChange={(e) => setPf("phone", e.target.value)} data-testid="profile-phone" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("dateOfBirth")}</Label><Input type="date" value={p.date_of_birth || ""} onChange={(e) => setPf("date_of_birth", e.target.value)} data-testid="profile-dob" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">{t("address")}</Label><Input value={p.address || ""} onChange={(e) => setPf("address", e.target.value)} data-testid="profile-address" /></div>
          </div>

          <div className="p-4 rounded-xl border border-border bg-muted/30 flex items-center justify-between" data-testid="profile-hourly-rate">
            <div><Label className="text-xs">{t("hourlyRate")} (CAD)</Label><p className="text-[11px] text-muted-foreground">{t("readOnlyField")}</p></div>
            <span className="text-lg font-bold text-primary">{user?.hourly_rate != null ? `$${Number(user.hourly_rate).toFixed(2)} / h` : "—"}</span>
          </div>

          <div className="space-y-1.5" data-testid="profile-positions">
            <Label className="text-xs">{t("assignedPositions")}</Label>
            {myPositions.length ? (
              <div className="flex flex-wrap gap-2">
                {myPositions.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${p.color}22`, color: p.color }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                  </span>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">—</p>}
            <p className="text-[11px] text-muted-foreground">{t("readOnlyField")}</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t("healthInfo")}</Label>
            <Textarea rows={3} value={p.health_info || ""} onChange={(e) => setPf("health_info", e.target.value)} placeholder={t("healthPlaceholder")} data-testid="profile-health" />
          </div>

          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t("emergencyContacts")}</Label>
              <Button size="sm" variant="outline" onClick={addContact} data-testid="add-contact"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
            </div>
            {(p.emergency_contacts || []).map((c, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-center">
                <Input placeholder={t("fullName")} value={c.name || ""} onChange={(e) => setContact(i, "name", e.target.value)} className="flex-1 min-w-32" />
                <Input placeholder={t("relationship")} value={c.relationship || ""} onChange={(e) => setContact(i, "relationship", e.target.value)} className="flex-1 min-w-32" />
                <Input placeholder={t("phone")} value={c.phone || ""} onChange={(e) => setContact(i, "phone", e.target.value)} className="flex-1 min-w-28" />
                <Button size="icon" variant="ghost" onClick={() => rmContact(i)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
          <Button onClick={saveProfile} data-testid="save-profile">{t("saveChanges")}</Button>
        </TabsContent>

        {/* SECURITY */}
        <TabsContent value="security" className="space-y-4 mt-6 max-w-md">
          <div className="space-y-1.5"><Label className="text-xs">{t("currentPassword")}</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} data-testid="current-password" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("newPassword")}</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} data-testid="new-password" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("confirmPassword")}</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} data-testid="confirm-password" /></div>
          <Button onClick={savePassword} data-testid="save-password">{t("changePassword")}</Button>
        </TabsContent>

        {/* PREFERENCES */}
        <TabsContent value="preferences" className="space-y-5 mt-6 max-w-md">
          <div className="flex items-center justify-between p-4 rounded-xl border border-border">
            <div className="flex items-center gap-3"><Globe className="w-5 h-5 text-primary" /><div><p className="font-medium text-sm">{t("language")}</p><p className="text-xs text-muted-foreground">Français / English</p></div></div>
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              <button onClick={() => setLang("fr")} className={`px-3 py-1 rounded-md text-sm font-medium ${lang === "fr" ? "bg-card shadow" : ""}`} data-testid="lang-fr">FR</button>
              <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-md text-sm font-medium ${lang === "en" ? "bg-card shadow" : ""}`} data-testid="lang-en">EN</button>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl border border-border">
            <div className="flex items-center gap-3">{theme === "dark" ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}<div><p className="font-medium text-sm">{t("theme")}</p><p className="text-xs text-muted-foreground">{theme === "dark" ? t("darkMode") : t("lightMode")}</p></div></div>
            <Button variant="outline" size="sm" onClick={toggleTheme} data-testid="toggle-theme">{theme === "dark" ? t("lightMode") : t("darkMode")}</Button>
          </div>
        </TabsContent>

        {/* BUSINESS */}
        {isAdmin && (
          <TabsContent value="business" className="space-y-4 mt-6 max-w-md">
            <p className="text-sm text-muted-foreground">{activeCenter?.name}</p>
            <div className="space-y-1.5"><Label className="text-xs">{t("businessName")}</Label><Input value={biz.name || ""} onChange={(e) => setBiz((b) => ({ ...b, name: e.target.value }))} data-testid="biz-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t("resourceCenterAddress")}</Label><Input value={biz.address || ""} onChange={(e) => setBiz((b) => ({ ...b, address: e.target.value }))} data-testid="biz-address" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("timezoneLabel")}</Label>
              <Select value={biz.timezone || "America/Toronto"} onValueChange={(v) => setBiz((b) => ({ ...b, timezone: v }))}>
                <SelectTrigger data-testid="biz-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={saveBusiness} data-testid="save-business">{t("saveChanges")}</Button>
            <div className="pt-4 border-t border-border"><PositionsManager /></div>
            <p className="text-xs text-muted-foreground pt-2">{isSuperAdmin && <>{t("moreBusinessSettings")} <button className="text-primary underline" onClick={() => navigate("/centers")}>{t("centers")}</button></>}</p>
          </TabsContent>
        )}

        {/* SYSTEM */}
        {isSuperAdmin && (
          <TabsContent value="system" className="space-y-4 mt-6">
            <p className="text-sm text-muted-foreground">{t("globalConfigDesc")}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => navigate("/centers")} className="text-left p-5 rounded-xl border border-border hover:border-primary/50 transition-colors" data-testid="sys-centers">
                <Building2 className="w-6 h-6 text-primary mb-2" />
                <p className="font-semibold">{t("centers")}</p>
                <p className="text-xs text-muted-foreground mt-1">{(centers || []).length} {t("centers").toLowerCase()}</p>
              </button>
              <button onClick={() => navigate("/platform")} className="text-left p-5 rounded-xl border border-border hover:border-primary/50 transition-colors" data-testid="sys-platform">
                <ShieldCheck className="w-6 h-6 text-primary mb-2" />
                <p className="font-semibold">{t("platform")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("globalConfig")}</p>
              </button>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
